// VRG-156 — the saved-customization affordance beside the "Customized" add-on
// button: a small rounded card (like a company preview) holding the front face,
// which opens a modal to view the design larger. Two-sided tags get a Front/Back
// toggle so the buyer can click through both faces.

import React, { useState } from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Button, Modal } from '@repo/ui'

import { TagFacePreview } from './TagFacePreview'
import type { LogoPlacement } from './TagCanvasEditor'

interface Face {
  logoDataUrl: string | null
  logoPlacement: LogoPlacement | null
}

interface TagDesignPreviewProps {
  shape: 'round' | 'rect'
  /** width / height aspect of the tag face (1 = square/round). */
  aspect: number
  tagColor: string
  /** Woven embroidered look — emboss the logo so it reads as raised thread. */
  embroidered?: boolean
  front: Face
  /** Back face — present only for 2-sided tags. */
  back?: Face | null
}

export const TagDesignPreview = ({
  shape,
  aspect,
  tagColor,
  embroidered = false,
  front,
  back,
}: TagDesignPreviewProps) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState<'front' | 'back'>('front')
  const hasBack = !!back
  const active = side === 'back' && back ? back : front

  // Cap the large preview so a wide rect tag doesn't overflow the modal — size is the
  // face HEIGHT, width = size × aspect, so bound width to ~260px for wide faces.
  const largeSize = Math.round(Math.min(240, 260 / (aspect > 0 ? aspect : 1)))

  const openModal = () => {
    setSide('front')
    setOpen(true)
  }

  return (
    <>
      {/* Rounded card affordance — mirrors the company-preview card treatment. */}
      <Box
        role="button"
        tabIndex={0}
        aria-label="View your custom design"
        onClick={openModal}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openModal()
          }
        }}
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          border: `1px solid ${theme.palette.primary[100]}`,
          backgroundColor: theme.palette.background.paper,
          cursor: 'pointer',
          outline: 'none',
          transition: 'border-color .15s, box-shadow .15s',
          '&:hover, &:focus-visible': {
            borderColor: theme.palette.brand[600],
            boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
          },
        }}
      >
        <TagFacePreview
          shape={shape}
          aspect={aspect}
          tagColor={tagColor}
          embroidered={embroidered}
          logoDataUrl={front.logoDataUrl}
          placement={front.logoPlacement}
          size={30}
        />
      </Box>

      <Modal title="Your design" open={open} onClose={() => setOpen(false)}>
        <Stack spacing={4} alignItems="center" sx={{ py: 2 }}>
          {/* Tapping the face flips to the other side on a 2-sided tag. */}
          <Box
            onClick={() => hasBack && setSide(s => (s === 'front' ? 'back' : 'front'))}
            sx={{ cursor: hasBack ? 'pointer' : 'default' }}
          >
            <TagFacePreview
              shape={shape}
              aspect={aspect}
              tagColor={tagColor}
              embroidered={embroidered}
              logoDataUrl={active.logoDataUrl}
              placement={active.logoPlacement}
              size={largeSize}
            />
          </Box>
          {hasBack ? (
            <Stack direction="row" spacing={3} alignItems="center">
              <Button
                variant={side === 'front' ? 'primary' : 'secondary'}
                disableElevation
                onClick={() => setSide('front')}
                sx={{ height: 34 }}
              >
                Front
              </Button>
              <Button
                variant={side === 'back' ? 'primary' : 'secondary'}
                disableElevation
                onClick={() => setSide('back')}
                sx={{ height: 34 }}
              >
                Back
              </Button>
            </Stack>
          ) : (
            <Typography variant="body3" color="text.secondary">
              Front
            </Typography>
          )}
        </Stack>
      </Modal>
    </>
  )
}
